import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { dreamsClient } from "../api/dreamsClient";
import {
  blockDreamEditorDrop,
  handlePlainTextPaste,
  sanitizeDreamHtml,
  scrubDreamEditor,
} from "../utils/safeDreamHtml";

const DRAFT_KEY = "morfeusz:add-sen:draft:v1";
const DREAM_EMOTIONS = ["Spokój", "Radość", "Strach", "Smutek", "Złość", "Zaskoczenie"];
const SAVE_MODE = "e2e";

const getAddDreamCopy = (lang) => ({
  gateEyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  gateTitlePre: lang === "en" ? "Welcome to" : "Witaj w",
  gateSubtitle: lang === "en" ? "Sign in to save a dream in your Dream Codex." : "Zaloguj się, aby zapisać sen w swojej Księdze Snów.",
  signIn: lang === "en" ? "🔑 Sign in" : "🔑 Zaloguj się",
  register: lang === "en" ? "✨ Create account" : "✨ Załóż konto",
  home: lang === "en" ? "🏠 Home" : "🏠 Strona główna",
  loadingEyebrow: lang === "en" ? "Dream Codex" : "Księga Snów",
  loadingTitlePre: lang === "en" ? "Checking the" : "Sprawdzam",
  loadingTitleAccent: lang === "en" ? "gate" : "bramę",
  loadingSubtitle: lang === "en" ? "The dream editor will open in a moment." : "Za chwilę otworzymy zapis snu.",
  pageTitle: lang === "en" ? "Add dream" : "Dodaj sen",
  pageSubtitle: lang === "en" ? "Save a dream in your Dream Codex" : "Zapisz sen w swojej Księdze Snów",
  titleLabel: lang === "en" ? "Dream title" : "Tytuł snu",
  titlePlaceholder: lang === "en" ? "Dream title" : "Tytuł snu",
  bodyLabel: lang === "en" ? "Dream body" : "Treść snu",
  bodyPlaceholder: lang === "en" ? "Describe your dream..." : "Opisz swój sen...",
  sideKicker: lang === "en" ? "Dream Codex" : "Księga Snów",
  sideTitle: lang === "en" ? "Entry settings" : "Ustawienia wpisu",
  dateLabel: lang === "en" ? "Dream date" : "Data snu",
  timeLabel: lang === "en" ? "Time" : "Godzina",
  emotionsLabel: lang === "en" ? "Emotions" : "Emocje",
  symbolsLabel: lang === "en" ? "Symbols" : "Symbole",
  symbolsPlaceholder: lang === "en" ? "e.g. train, bridge, rain" : "np. pociąg, most, deszcz",
  saveHelp: lang === "en"
    ? "Dreams in the Room are saved in the E2E vault. Morfeusz does not know the vault password and cannot recover it."
    : "Sny w Pokoiku są zapisywane w sejfie E2E. Morfeusz nie zna hasła sejfu i nie może go odzyskać.",
  draftHint: lang === "en"
    ? "The draft is saved locally in this browser, but without the dream body."
    : "Szkic zapisuje się lokalnie w tej przeglądarce, ale bez treści snu.",
  missingTitle: lang === "en" ? "Enter a dream title." : "Podaj tytuł snu.",
  missingBody: lang === "en" ? "Describe the dream before saving." : "Opisz sen przed zapisem.",
  vaultNotReady: lang === "en" ? "The E2E vault is not ready yet. Refresh the page and try again." : "Sejf E2E nie jest jeszcze gotowy. Odśwież stronę i spróbuj ponownie.",
  unlocking: lang === "en" ? "Unlocking the vault…" : "Odblokowuję sejf…",
  unlockFailed: lang === "en" ? "Could not unlock the vault. The dream was not saved." : "Nie udało się odblokować sejfu. Sen nie został zapisany.",
  encrypting: lang === "en" ? "Encrypting the dream…" : "Szyfruję sen…",
  saving: lang === "en" ? "Saving the dream…" : "Zapisuję sen…",
  saved: lang === "en" ? "Dream saved in the vault. After saving, open the dream view." : "Sen zapisany w sejfie. Po zapisie przejdź do widoku snu.",
  sessionExpired: lang === "en" ? "Session expired. Sign in again to save the dream." : "Sesja wygasła. Zaloguj się ponownie, aby zapisać sen.",
  saveFailed: lang === "en" ? "Could not save the dream. Try again." : "Nie udało się zapisać snu. Spróbuj ponownie.",
  draftCleared: lang === "en" ? "Draft cleared." : "Wyczyszczono szkic.",
  goToDream: lang === "en" ? "Go to dream view" : "Przejdź do widoku snu",
  submitSaving: lang === "en" ? "Saving…" : "Zapisuję…",
  submit: lang === "en" ? "Save dream" : "Zapisz sen",
  clearDraft: lang === "en" ? "Clear draft" : "Wyczyść szkic",
  backRoom: lang === "en" ? "Back to Room" : "Wróć do Pokoiku",
});

const getEmotionLabel = (emotion, lang) => {
  if (lang !== "en") return emotion;
  const map = {
    Spokój: "Calm",
    Radość: "Joy",
    Strach: "Fear",
    Smutek: "Sadness",
    Złość: "Anger",
    Zaskoczenie: "Surprise",
  };
  return map[emotion] || emotion;
};

const today = () => new Date().toISOString().slice(0, 10);
const nowTime = () => new Date().toTimeString().slice(0, 5);

const readDraft = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.bodyHtml || parsed?.sen_opis) {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify({
        ...parsed,
        bodyHtml: "",
        sen_opis: "",
        bodyDraftRemoved: true,
        bodyDraftRemovedAt: Date.now(),
      }));
    }
    return parsed ? { ...parsed, bodyHtml: "", sen_opis: "" } : null;
  } catch (_) {
    return null;
  }
};

const writeDraft = (draft) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (_) {}
};

const clearDraft = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch (_) {}
};

const useSettingsColumnHeight = (gridRef, sideRef, deps = []) => {
  useEffect(() => {
    const grid = gridRef.current;
    const side = sideRef.current;
    if (!grid || !side || typeof window === "undefined") return undefined;

    let frame = 0;
    const applyHeight = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (window.matchMedia("(max-width: 900px)").matches) {
          grid.style.removeProperty("--dream-settings-height");
          return;
        }

        const height = Math.ceil(side.getBoundingClientRect().height);
        if (height > 0) {
          grid.style.setProperty("--dream-settings-height", `${height}px`);
        }
      });
    };

    applyHeight();

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(applyHeight) : null;
    observer?.observe(side);
    window.addEventListener("resize", applyHeight);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", applyHeight);
    };
  }, deps);
};

const PokoikGate = ({ copy }) => (
  <section className="mdPage" data-ui="gate">
    <div className="mdPage__inner">
      <div>
        <p className="mdPage__eyebrow">{copy.gateEyebrow}</p>
        <h1 className="mdPage__title">{copy.gateTitlePre} <em>Morfeusz Dreams</em></h1>
        <p className="mdPage__subtitle">
          {copy.gateSubtitle}
        </p>
      </div>

      <div className="mdCard">
        <div className="mdRow">
          <a className="mdBtn mdBtn--primary mdBtn--row" data-md-login="1" href="#/zaloguj?redirect_to=/dodaj-sen">
            {copy.signIn}
          </a>
          <a className="mdBtn mdBtn--row" href="#/rejestracja?redirect_to=/dodaj-sen">
            {copy.register}
          </a>
          <a className="mdBtn mdBtn--row" href="#top">
            {copy.home}
          </a>
        </div>
      </div>
    </div>
  </section>
);

const LoadingGate = ({ copy }) => (
  <section className="mdPage" data-ui="add-dream-loading">
    <div className="mdPage__inner">
      <div className="mdCard">
        <p className="mdPage__eyebrow">{copy.loadingEyebrow}</p>
        <h1 className="mdPage__title">{copy.loadingTitlePre} <em>{copy.loadingTitleAccent}</em></h1>
        <p className="mdPage__subtitle">{copy.loadingSubtitle}</p>
      </div>
    </div>
  </section>
);

export const AddDreamPage = () => {
  const { lang } = useLang();
  const { isAuthenticated, authReady } = useAuth();
  const copy = getAddDreamCopy(lang);
  const editorRef = useRef(null);
  const formGridRef = useRef(null);
  const sideRef = useRef(null);
  const initialDraft = readDraft();
  const [title, setTitle] = useState(initialDraft?.title || initialDraft?.sen_tytul || "");
  const [date, setDate] = useState(initialDraft?.date || initialDraft?.sen_data || today());
  const [time, setTime] = useState(initialDraft?.time || initialDraft?.sen_godzina || nowTime());
  const [symbols, setSymbols] = useState(initialDraft?.symbols || initialDraft?.sen_symbole || "");
  const [emotions, setEmotions] = useState(
    Array.isArray(initialDraft?.emotions)
      ? initialDraft.emotions
      : Array.isArray(initialDraft?.sen_emocje)
        ? initialDraft.sen_emocje
        : [],
  );
  const [status, setStatus] = useState("");
  const [savedHref, setSavedHref] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [toolbarState, setToolbarState] = useState({
    bold: false,
    italic: false,
    underline: false,
    hrFlash: false,
  });

  useSettingsColumnHeight(formGridRef, sideRef, [emotions.length, status]);

  if (!authReady) return <LoadingGate copy={copy} />;
  if (!isAuthenticated) return <PokoikGate copy={copy} />;

  const syncDraft = (overrides = {}, options = {}) => {
    const editor = editorRef.current;
    if (options.scrubDom && editor) {
      scrubDreamEditor(editor);
    }
    const bodyHtml = sanitizeDreamHtml(editor?.innerHTML || "");
    if (options.rewriteDom && editor && editor.innerHTML !== bodyHtml) {
      editor.innerHTML = bodyHtml;
    }

    const next = {
      title,
      date,
      time,
      symbols,
      emotions,
      saveMode: SAVE_MODE,
      ...overrides,
    };

    writeDraft({
      ...next,
      v: 2,
      savedAt: Date.now(),
      sen_tytul: next.title,
      sen_opis: "",
      sen_data: next.date,
      sen_godzina: next.time,
      sen_symbole: next.symbols,
      sen_emocje: next.emotions,
      save_mode: SAVE_MODE,
      bodyDraftStored: false,
    });
  };

  const readFormatState = () => {
    try {
      return {
        bold: !!document.queryCommandState("bold"),
        italic: !!document.queryCommandState("italic"),
        underline: !!document.queryCommandState("underline"),
      };
    } catch (_) {
      return null;
    }
  };

  const runCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncDraft({}, { scrubDom: true });

    if (command === "bold" || command === "italic" || command === "underline") {
      window.requestAnimationFrame(() => {
        const next = readFormatState();
        if (!next) return;
        setToolbarState((prev) => ({ ...prev, ...next }));
      });
      return;
    }

    if (command === "insertHorizontalRule") {
      setToolbarState((prev) => ({ ...prev, hrFlash: true }));
      window.setTimeout(() => {
        setToolbarState((prev) => ({ ...prev, hrFlash: false }));
      }, 220);
    }
  };

  const handleToolbarMouseDown = (event, command) => {
    event.preventDefault();
    runCommand(command);
  };

  const refreshToolbarState = () => {
    const next = readFormatState();
    if (!next) return;
    setToolbarState((prev) => ({ ...prev, ...next }));
  };

  const handleInput = () => {
    syncDraft();
    refreshToolbarState();
  };

  const handlePaste = (event) => {
    const handled = handlePlainTextPaste(event, editorRef.current);
    if (handled) {
      syncDraft({}, { scrubDom: true });
      refreshToolbarState();
    }
  };

  const handleDrop = (event) => {
    blockDreamEditorDrop(event, editorRef.current);
    syncDraft({}, { scrubDom: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) return;
    const editor = editorRef.current;
    if (editor) scrubDreamEditor(editor);
    const bodyHtml = sanitizeDreamHtml(editor?.innerHTML || "");
    if (editor && editor.innerHTML !== bodyHtml) editor.innerHTML = bodyHtml;
    const bodyText = editor?.innerText?.trim() || "";

    if (!title.trim()) {
      setStatus(copy.missingTitle);
      return;
    }

    if (!bodyText) {
      setStatus(copy.missingBody);
      return;
    }

    if (!window.MDVaultSession || !window.morfeuszE2E) {
      setStatus(copy.vaultNotReady);
      return;
    }

    setIsSaving(true);
    setStatus(copy.unlocking);
    setSavedHref("");

    try {
      const unlocked = await window.MDVaultSession.ensureUnlocked();
      if (!unlocked?.ok && !unlocked?.state?.unlocked) {
        setStatus(copy.unlockFailed);
        return;
      }

      setStatus(copy.encrypting);
      const encrypted = await window.morfeuszE2E.encryptText(bodyHtml);

      setStatus(copy.saving);
      const response = await dreamsClient.create({
        title: title.trim(),
        date,
        time,
        saveMode: SAVE_MODE,
        bodyHtml: "",
        e2e: encrypted,
        symbols: symbols.trim(),
        emotions,
        sen_tytul: title.trim(),
        sen_opis: "",
        sen_data: date,
        sen_godzina: time,
        sen_symbole: symbols.trim(),
        sen_emocje: emotions,
        save_mode: SAVE_MODE,
      });
      const dreamId = response?.dream?.id || response?.dream?.wpId;
      clearDraft();
      setSavedHref(dreamId ? `#/sen/${dreamId}` : "#/moje-sny");
      setStatus(copy.saved);
    } catch (error) {
      if (error?.code === "unauthorized") {
        setStatus(copy.sessionExpired);
      } else {
        setStatus(error?.message || copy.saveFailed);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (setter, key) => (event) => {
    const value = event.target.value;
    setter(value);
    window.setTimeout(() => syncDraft({ [key]: value }), 0);
  };

  const toggleEmotion = (emotion) => {
    setEmotions((prev) => {
      const next = prev.includes(emotion)
        ? prev.filter((item) => item !== emotion)
        : [...prev, emotion];
      window.setTimeout(() => syncDraft({ emotions: next }), 0);
      return next;
    });
  };

  const handleClearDraft = () => {
    clearDraft();
    setTitle("");
    setDate(today());
    setTime(nowTime());
    setSymbols("");
    setEmotions([]);
    setStatus(copy.draftCleared);
    setSavedHref("");
    setToolbarState({ bold: false, italic: false, underline: false, hrFlash: false });
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  return (
    <section className="dream-container" id="dodaj-sen" data-ui="add-dream-root">
      <header className="dream-header">
        <div className="dream-icon" aria-hidden="true">
          🌙
        </div>
        <h1 className="dream-title">{copy.pageTitle}</h1>
        <div className="dream-date">{copy.pageSubtitle}</div>
      </header>

      <form className="dream-content-card" data-ui="add-dream-form" onSubmit={handleSubmit}>
        <div className="dream-form-grid" ref={formGridRef}>
          <div data-ui="add-dream-main">
            <div className="field">
              <label htmlFor="dream-title-input">{copy.titleLabel}</label>
              <input
                id="dream-title-input"
                className="md-input md-title-input"
                type="text"
                value={title}
                onChange={handleFieldChange(setTitle, "title")}
                placeholder={copy.titlePlaceholder}
              />
            </div>

            <div className="field">
              <label>{copy.bodyLabel}</label>
              <div className="md-toolbar" data-ui="add-dream-toolbar">
                <button
                  type="button"
                  className={`md-btn ${toolbarState.bold ? "is-active" : ""}`}
                  onMouseDown={(event) => handleToolbarMouseDown(event, "bold")}
                >
                  B
                </button>
                <button
                  type="button"
                  className={`md-btn ${toolbarState.italic ? "is-active" : ""}`}
                  onMouseDown={(event) => handleToolbarMouseDown(event, "italic")}
                >
                  I
                </button>
                <button
                  type="button"
                  className={`md-btn ${toolbarState.underline ? "is-active" : ""}`}
                  onMouseDown={(event) => handleToolbarMouseDown(event, "underline")}
                >
                  U
                </button>
                <button
                  type="button"
                  className={`md-btn ${toolbarState.hrFlash ? "is-active" : ""}`}
                  onMouseDown={(event) => handleToolbarMouseDown(event, "insertHorizontalRule")}
                >
                  HR
                </button>
              </div>
              <div
                ref={editorRef}
                className="dream-editor"
                contentEditable
                data-placeholder={copy.bodyPlaceholder}
                onInput={handleInput}
                onPaste={handlePaste}
                onDrop={handleDrop}
                onDragOver={handleDrop}
                onKeyUp={refreshToolbarState}
                onMouseUp={refreshToolbarState}
                onFocus={refreshToolbarState}
                suppressContentEditableWarning
              />
            </div>
          </div>

          <aside className="md-card" data-ui="add-dream-side" ref={sideRef}>
            <div className="add-dream-side-head" data-ui="add-dream-side-head">
              <div className="add-dream-side-kicker">{copy.sideKicker}</div>
              <h2>{copy.sideTitle}</h2>
            </div>

            <div className="field">
              <label htmlFor="dream-date-input">{copy.dateLabel}</label>
              <input
                id="dream-date-input"
                className="md-input"
                type="date"
                value={date}
                onChange={handleFieldChange(setDate, "date")}
              />
            </div>

            <div className="field">
              <label htmlFor="dream-time-input">{copy.timeLabel}</label>
              <input
                id="dream-time-input"
                className="md-input"
                type="time"
                value={time}
                onChange={handleFieldChange(setTime, "time")}
              />
            </div>

            <div className="field">
              <label>{copy.emotionsLabel}</label>
              <div className="md-chips" id="md-emocje" data-ui="add-dream-emotions" data-name="sen_emocje[]">
                {DREAM_EMOTIONS.map((emotion) => (
                  <button
                    key={emotion}
                    type="button"
                    className={`md-chip-btn ${emotions.includes(emotion) ? "active" : ""}`}
                    data-val={emotion}
                    onClick={() => toggleEmotion(emotion)}
                  >
                    {getEmotionLabel(emotion, lang)}
                  </button>
                ))}
              </div>
              <div id="md-emocje-hidden" aria-hidden="true" hidden>
                {emotions.map((emotion) => (
                  <input key={emotion} type="hidden" name="sen_emocje[]" value={emotion} readOnly />
                ))}
              </div>
            </div>

            <div className="field">
              <label htmlFor="dream-symbols-input">{copy.symbolsLabel}</label>
              <input
                id="dream-symbols-input"
                className="md-input"
                type="text"
                value={symbols}
                onChange={handleFieldChange(setSymbols, "symbols")}
                placeholder={copy.symbolsPlaceholder}
              />
            </div>

            <div className="md-hint" data-ui="add-dream-save-help">
              {copy.saveHelp}
            </div>

            <div className="md-hint" data-ui="add-dream-draft-hint">
              {copy.draftHint}
            </div>
          </aside>
        </div>

        {status && (
          <div className="md-alert md-info" data-ui="add-dream-status" role="status">
            <div data-ui="add-dream-status-text">{status}</div>
            {savedHref && (
              <div className="md-cta-row" data-ui="add-dream-status-actions">
                <a className="md-btn md-secondary" href={savedHref}>
                  {copy.goToDream}
                </a>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="md-btn md-primary" disabled={isSaving}>
            {isSaving ? copy.submitSaving : copy.submit}
          </button>
          <button type="button" className="md-btn md-secondary" onClick={handleClearDraft}>
            {copy.clearDraft}
          </button>
          <a className="md-btn md-secondary" href="#/pokoik">
            {copy.backRoom}
          </a>
        </div>
      </form>
    </section>
  );
};
